import React from "react";
import {
  COMMENT_SCOPE,
  MEMBER_STATE,
  MEMBER_STATE_LABEL,
  OBJECTIVE_TYPE,
  isNotSubmittedByMember,
} from "../../_config/quarterlyOkrConfig";
import {
  QokrAvatar,
  personLabel,
} from "../../_shared/QokrPrimitives/QokrPrimitives";
import ReviewCommentThread from "../ReviewCommentThread/ReviewCommentThread";
import ReviewObjectiveRow from "../ReviewObjectiveRow/ReviewObjectiveRow";
import {
  commentKey,
  commentsFor,
  deletingCommentId,
} from "../useReviewActions";
import "./_review-individual-okrs.scss";

// ═══════════════════════════════════════════════════════════════════════════
// "Individual OKRs" tab (§9.3): every person on the quarter, each with what
// the contract asks the coach to notice about them, a comment thread for
// them, and their own objectives. People with no objectives still appear —
// "didn't submit" is exactly what the coach is looking for.
// ═══════════════════════════════════════════════════════════════════════════

// Plain-language flags for one member, in the order a coach should read them.
const memberFlags = (member) => {
  const flags = [];
  if (member.is_deactivated) flags.push("Deactivated");
  if (member.joined_after_send) flags.push("Joined after the send");
  if (isNotSubmittedByMember(member.submission_kind)) {
    flags.push("Didn't submit — sent early");
  }
  if (member.no_assignment) flags.push("No company objective assigned");
  if ((member.owner_without_individual_objective || []).length) {
    flags.push("Owns a company objective with no individual objective");
  }
  return flags;
};

const ReviewIndividualOkrs = ({ review, rowProps, actions, readOnly }) => {
  const members = review.members || [];
  const companyLabel = new Map(
    (review.company_objectives || []).map((o) => [
      String(o.objective_id),
      o.objective,
    ]),
  );
  const objectivesOf = (userId) =>
    (review.individual_objectives || []).filter(
      (o) => String(o.member?.user_id) === String(userId),
    );

  if (!members.length) {
    return <div className="aokr-empty">No one is on this quarter yet.</div>;
  }

  return (
    <div className="qokr-review-people">
      {members.map((member) => {
        const objectives = objectivesOf(member.user_id);
        const flags = memberFlags(member);
        const target = { scope: COMMENT_SCOPE.MEMBER, userId: member.user_id };
        const submitted = member.member_state === MEMBER_STATE.SUBMITTED;

        return (
          <section
            key={member.user_id}
            className="qokr-review-person"
            data-review-member={member.user_id}
          >
            <div className="qokr-review-person__card">
              <div className="qokr-review-person__head">
                <QokrAvatar person={member} />
                <div className="qokr-review-person__who">
                  <span className="qokr-review-person__name">
                    {personLabel(member)}
                    {member.is_ceo ? " (CEO)" : ""}
                  </span>
                  {member.role ? (
                    <span className="qokr-review-person__role">
                      {member.role}
                    </span>
                  ) : null}
                </div>
                <span
                  className={`qokr-pill ${
                    submitted ? "qokr-pill--green" : "qokr-pill--grey"
                  }`}
                >
                  {MEMBER_STATE_LABEL[member.member_state] ||
                    member.member_state}
                </span>
              </div>

              {flags.length ? (
                <div className="qokr-review-person__flags">
                  {flags.map((flag) => (
                    <span key={flag} className="qokr-pill qokr-pill--amber">
                      {flag}
                    </span>
                  ))}
                </div>
              ) : null}

              <ReviewCommentThread
                comments={commentsFor(review.comments, target)}
                readOnly={readOnly}
                addLabel={`Add a comment about ${member.name}`}
                placeholder={`What should ${member.name} and the CEO talk about?`}
                saving={Boolean(actions.pending[commentKey(target)])}
                deletingId={deletingCommentId(actions.pending)}
                onAdd={(text) => actions.addComment(target, text)}
                onDelete={actions.deleteComment}
              />
            </div>

            {objectives.length ? (
              <div className="rm-rows">
                {objectives.map((objective) => (
                  <ReviewObjectiveRow
                    key={objective.objective_id}
                    {...rowProps(OBJECTIVE_TYPE.INDIVIDUAL, objective, {
                      linkLabel: companyLabel.get(
                        String(objective.company_objective_id),
                      ),
                    })}
                  />
                ))}
              </div>
            ) : (
              <p className="qokr-review-person__none">
                {member.name} has no individual objectives.
              </p>
            )}
          </section>
        );
      })}
    </div>
  );
};

export default ReviewIndividualOkrs;
